/**
 * Account Targets View
 * Displays and manages per-account balance targets
 */
import { useAccountTargets, useDeleteAccountTarget } from '@/hooks/useAllocation';
import { useAllocationStore } from '@/store/allocationStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  TARGET_TYPE_DISPLAY_NAMES,
  type AccountWithTarget,
  type TargetType,
} from '@/types/allocation';

export default function AccountTargetsView() {
  const { data, isLoading } = useAccountTargets();
  const setEditingAccountId = useAllocationStore((state) => state.setEditingAccountId);

  if (isLoading || !data) {
    return <TargetsSkeleton />;
  }

  const { targets, summary } = data;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Account Targets
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Set balance targets for individual accounts
          </p>

          <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-4">
            <SummaryItem
              label="With Targets"
              value={summary.totalWithTargets}
              color="text-gray-900"
            />
            <SummaryItem
              label="On Target"
              value={summary.onTarget}
              color="text-green-600"
            />
            <SummaryItem
              label="Below Target"
              value={summary.belowTarget}
              color="text-red-600"
            />
            <SummaryItem
              label="Above Target"
              value={summary.aboveTarget}
              color="text-blue-600"
            />
          </div>
        </div>
      </Card>

      {/* Targets list */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Account Targets
            </h3>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setEditingAccountId('new')}
            >
              Add Target
            </Button>
          </div>

          {targets.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Balance
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target Range
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {targets.map((accountWithTarget) => (
                    <TargetRow
                      key={accountWithTarget.accountId}
                      accountWithTarget={accountWithTarget}
                      onEdit={() => setEditingAccountId(accountWithTarget.accountId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

interface SummaryItemProps {
  label: string;
  value: number;
  color: string;
}

function SummaryItem({ label, value, color }: SummaryItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

interface TargetRowProps {
  accountWithTarget: AccountWithTarget;
  onEdit: () => void;
}

function TargetRow({ accountWithTarget, onEdit }: TargetRowProps) {
  const { mutate: deleteTarget, isPending } = useDeleteAccountTarget();
  const target = accountWithTarget.target;

  const handleDelete = () => {
    if (confirm('Are you sure you want to remove this target?')) {
      deleteTarget(accountWithTarget.accountId);
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-4 whitespace-nowrap">
        <div>
          <p className="text-sm font-medium text-gray-900">{accountWithTarget.accountName}</p>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        <span className="text-sm font-medium text-gray-900">
          ${formatNumber(accountWithTarget.currentBalanceUsd ?? '0')}
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-center">
        {target && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {TARGET_TYPE_DISPLAY_NAMES[target.targetType]}
          </span>
        )}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        <span className="text-sm text-gray-600">
          {target ? formatTargetRange(target.targetType, target.targetValue, target.targetValueMax) : '-'}
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-center">
        <StatusBadge targetStatus={accountWithTarget.targetStatus} difference={accountWithTarget.difference} />
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            isLoading={isPending}
            className="text-red-600 hover:text-red-700"
          >
            Delete
          </Button>
        </div>
      </td>
    </tr>
  );
}

interface StatusBadgeProps {
  targetStatus: 'below_target' | 'on_target' | 'above_target' | null;
  difference: string | null;
}

function StatusBadge({ targetStatus, difference }: StatusBadgeProps) {
  if (!targetStatus) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  if (targetStatus === 'on_target') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        On Target
      </span>
    );
  }

  const isBelow = targetStatus === 'below_target';
  const colorClasses = isBelow
    ? 'bg-red-100 text-red-800'
    : 'bg-blue-100 text-blue-800';
  const diffValue = difference ? Math.abs(parseFloat(difference)) : 0;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
      {isBelow ? 'Below' : 'Above'} by ${formatNumber(diffValue)}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
        <TargetIcon className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="mt-4 text-sm font-medium text-gray-900">
        No account targets set
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Set targets to track whether accounts are on track.
      </p>
    </div>
  );
}

function TargetsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
      <Card>
        <div className="p-6 animate-pulse">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function TargetIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
      />
    </svg>
  );
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatTargetRange(
  targetType: TargetType,
  targetValue: string,
  targetValueMax: string | null
): string {
  if (targetType === 'months_expenses') {
    return `${targetValue} months`;
  }

  if (targetType === 'percentage_income') {
    return `${targetValue}%`;
  }

  const value = formatNumber(targetValue);

  if (targetType === 'range' && targetValueMax) {
    const max = formatNumber(targetValueMax);
    return `$${value} - $${max}`;
  }

  return `$${value}`;
}
